import { useEffect } from "react";
import { Check } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import QuestionCard from "@/components/site/QuestionCard";
import ContactForm from "@/components/site/ContactForm";

const ACCENT = "#004D40"; // teal

const QUESTIONS = [
  {
    q: "Could that student turn off the water main during a burst pipe, or put out an oil fire correctly — or would instinct take over?",
    f: "Academic ranking and practical readiness are completely separate skills. One is measured constantly; the other is almost never taught.",
  },
  {
    q: "Would that student recognise the fraud that took ₹45 lakh from a retired IAS officer in 2025?",
    f: "If the most educated adults are being caught, marks are clearly not the protection. A specific, teachable reflex is.",
  },
  {
    q: "Could that student explain why ₹500 saved at 17 becomes ₹78 lakh by 60 — but only ₹24 lakh if they start at 27?",
    f: "The single most powerful idea in personal finance — and it appears nowhere in the curriculum a school is judged on.",
  },
  {
    q: "A decade after leaving school, what will students thank the school for — the exam they cleared, or the life skill that protected them?",
    f: "Schools are remembered for the capability they built, long after the marks are forgotten.",
  },
];

const NOT_THIS = [
  { not: "Not extra academic load.", but: "One period a week, built around real situations — not another exam to prepare for." },
  { not: "Not a threat to board results.", but: "Designed for Classes 5–9 where there is room to build, not to compete with board-exam years." },
  { not: "Not dependent on special infrastructure.", but: "A classroom, a facilitator, and the textbook. Nothing else required." },
  { not: "Not another thing teachers must figure out alone.", but: "Complete teacher training and certification are included. The lesson guide makes any teacher an expert before they enter the room." },
];

const GIVES = [
  "A genuinely differentiated offering — something no neighbouring school can claim.",
  "Visible alignment with NEP 2020's call for life skills, experiential and competency-based learning (Sections 4.6, 4.23, 5.14).",
  "Parents who see real change at home — your most powerful source of word-of-mouth.",
  "Teacher development — facilitators grow into confident experts on topics they were never trained in.",
  "A measurable outcomes story you can show management and parents every term.",
];

const STEPS = [
  { n: "01", t: "A conversation", d: "We listen to your school's specific context. No pitch." },
  { n: "02", t: "A pilot", d: "One class, one level, one term. At cost or free." },
  { n: "03", t: "Measured honestly", d: "Before-and-after, plus teacher and parent feedback." },
  { n: "04", t: "Decide from evidence", d: "Expand only if the pilot proves its worth." },
];

export default function ForSchools() {
  useReveal();
  useEffect(() => { document.title = "For Schools — LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section className="bg-[#0D1B2A] text-white relative">
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: ACCENT }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#F57F17" }}>For Schools</div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[64px] leading-[1.05] tracking-tight">
              Your students are already excellent. This is the part the syllabus can't reach.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              LifeIQ is not a replacement for academics or a burden on board-exam preparation. It is one period a week that gives students the practical life capability that no subject currently covers — delivered with full teacher training, ready to run next term.
            </p>
          </div>
        </div>
      </section>

      {/* DISCOVERY QUESTIONS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>A question worth sitting with</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Imagine your strongest student — top of the class, board-exam ready.
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5C6470] leading-[1.7]">Now ask honestly:</p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {QUESTIONS.map((c, i) => <QuestionCard key={i} q={c.q} f={c.f} i={i} accent={ACCENT} />)}
          </div>

          <div className="mt-16 max-w-3xl reveal">
            <p className="font-serif-d text-[26px] sm:text-[32px] leading-[1.3]" style={{ color: ACCENT }}>
              None of this competes with academics. It completes them. LifeIQ is the layer your strongest curriculum was never designed to carry.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT IT IS NOT */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Clearing the air</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              First, what LifeIQ is not.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {NOT_THIS.map((c, i) => (
              <div key={c.not} data-testid={`notthis-${i}`} className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7" style={{ transitionDelay: `${(i % 2) * 80}ms` }}>
                <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Not this</div>
                <div className="mt-2 font-serif-d text-[22px] text-[#0D1B2A] leading-tight">{c.not}</div>
                <div className="mt-5 pt-5 border-t border-[#E5E7EA]">
                  <div className="text-[12px] uppercase tracking-[0.22em] text-[#5C6470] font-semibold">But this</div>
                  <p className="mt-2 text-[16px] text-[#1A1A1A] leading-[1.7]">{c.but}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IT GIVES YOUR SCHOOL */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What partnering with LifeIQ gives you</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Five things schools tell us they value most.
            </h2>
          </div>

          <ul className="mt-12 space-y-5 reveal">
            {GIVES.map((g, i) => (
              <li key={i} data-testid={`gives-${i}`} className="flex items-start gap-4 border-b border-[#E5E7EA] pb-5 last:border-b-0">
                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-1" style={{ backgroundColor: ACCENT, color: "#fff" }}>
                  <Check size={14} />
                </span>
                <span className="text-[17px] sm:text-[19px] text-[#0D1B2A] leading-[1.55]">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TEACHER TRAINING */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Teacher training</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Your teachers are trained, not left to cope.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Every LifeIQ facilitator completes a structured two-day certification programme before teaching a single class. They learn the facilitation method, master the content level by level, and practise handling real classroom situations. The result is a Certified LifeIQ Facilitator who teaches with genuine confidence — because the curriculum makes them an expert, not a reader of slides.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS PRACTICALLY */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>How it works, practically</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              How a school starts — without risk.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.n} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="font-serif-d text-[38px] leading-none" style={{ color: ACCENT }}>{s.n}</div>
                <div className="mt-3 font-serif-d text-[20px] text-[#0D1B2A]">{s.t}</div>
                <p className="mt-2 text-[15px] text-[#5C6470] leading-[1.65]">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 reveal border-t border-[#E5E7EA] pt-8 text-[15px] text-[#5C6470] leading-[1.7] max-w-3xl">
            Pricing and delivery are worked out with each school individually — based on the level chosen, the number of classes, and how you'd like the programme delivered. We keep it simple and affordable, and we'll discuss it openly once a pilot has shown you the value.
          </div>
        </div>
      </section>

      {/* CTA + FORM */}
      <section id="contact" className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5 reveal">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Talk to us</div>
              <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[52px] leading-[1.06] text-white">
                Start with one honest conversation.
              </h2>
              <p className="mt-7 text-[17px] sm:text-[19px] text-white/80 leading-[1.7] max-w-md">
                Tell us about your school. We will not pitch — we will listen, and show you exactly how a pilot would work in your context.
              </p>
            </div>
            <div className="md:col-span-7 reveal">
              <ContactForm
                idPrefix="schools-contact"
                theme="dark"
                roles={["Principal", "Vice Principal", "Academic Head", "Correspondent", "Teacher", "Other"]}
                roleLabel="Your role"
                schoolLabel="School name"
                schoolPlaceholder="School name"
                submitLabel="Request a Pilot Conversation"
                source="for-schools"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
